import { Injectable, effect, inject, linkedSignal } from '@angular/core';
import { UserScopedStorageService } from './user-scoped-storage.service';

export type Theme = 'light' | 'dark';

const THEME_STORAGE_KEY = 'smartcapacity.theme';

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  private readonly userStorage = inject(UserScopedStorageService);

  // Auto-recomputes on user identity change: userStorage.get transitively reads USER_ID_PROVIDER (auth.currentUser signal).
  private readonly _currentTheme = linkedSignal<Theme>(() => {
    const stored = this.userStorage.get(THEME_STORAGE_KEY);
    return stored === 'light' || stored === 'dark' ? stored : this.getSystemTheme();
  });

  readonly currentTheme = this._currentTheme.asReadonly();

  constructor() {
    // Boot runs pre-auth under the anon scope; re-apply once the identity-scoped value resolves.
    effect(() => this.applyThemeToDocument(this._currentTheme()));
  }

  /**
   * Initialize theme from localStorage or system preference.
   * Returns the detected/selected theme.
   */
  initializeTheme(): Theme {
    const theme = this._currentTheme();
    this.applyThemeToDocument(theme);
    return theme;
  }

  /**
   * Toggle between light and dark themes.
   */
  toggleTheme(): void {
    const newTheme: Theme = this._currentTheme() === 'light' ? 'dark' : 'light';
    this.setTheme(newTheme);
  }

  /**
   * Set the current theme and persist to localStorage.
   */
  setTheme(theme: Theme): void {
    // Guard the trust boundary: callers cast server strings (e.g. legacy 'system') via `as Theme`.
    // Persisting them verbatim would poison storage with a value the boot read rejects, so resolve first.
    const resolved: Theme = theme === 'light' || theme === 'dark' ? theme : this.getSystemTheme();
    this._currentTheme.set(resolved);
    this.saveThemeToStorage(resolved);
    this.applyThemeToDocument(resolved);
  }

  /**
   * Get the current theme.
   */
  getCurrentTheme(): Theme {
    return this._currentTheme();
  }

  hasStoredTheme(): boolean {
    return this.userStorage.get(THEME_STORAGE_KEY) !== null;
  }

  /**
   * Re-apply the identity-scoped theme (e.g. after sign-in/sign-out).
   */
  applyCurrentTheme(): void {
    this.applyThemeToDocument(this._currentTheme());
  }

  /**
   * Apply theme to document root element.
   */
  applyThemeToDocument(theme: Theme): void {
    if (typeof document !== 'undefined') {
      document.documentElement.dataset['theme'] = theme;
    }
  }

  /**
   * Detect system theme preference.
   */
  private getSystemTheme(): Theme {
    if (globalThis.window !== undefined && globalThis.matchMedia) {
      return globalThis.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return 'light'; // Default fallback
  }

  private saveThemeToStorage(theme: Theme): void {
    this.userStorage.set(THEME_STORAGE_KEY, theme);
  }
}
